import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import useRecommendationsApi from '../../api/recommendations';
import { useUser } from './UserContext';

const SelectionContext = createContext(null);
const MAX_SELECTION = 10;

const storageKey = (userId) => `mv_selection_${userId ?? 'guest'}`;

const load = (userId) => {
  try {
    const raw = localStorage.getItem(storageKey(userId));
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
};

export function SelectionProvider({ children }) {
  const { trackEvents } = useRecommendationsApi();
  const { user, loading, isAuthenticated } = useUser();
  const userId = user?.id;

  // selected : tableau d'annonces complètes (utile pour la page "Mes demandes")
  const [selected, setSelected] = useState(() => load(userId));
  const selectedRef = useRef(selected);

  useEffect(() => {
    selectedRef.current = selected;
  }, [selected]);

  // Recharge au changement d'utilisateur
  useEffect(() => {
    setSelected(load(userId));
  }, [userId]);

  // Persistance
  useEffect(() => {
    try {
      localStorage.setItem(storageKey(userId), JSON.stringify(selected));
    } catch {
      /* quota ou navigation privée : on ignore */
    }
  }, [selected, userId]);

  const toggleSelect = useCallback(
    (annonce) => {
      const id = annonce?.vehiculeAnnonceId;
      if (!id) return;

      const exists = selectedRef.current.some((a) => a.vehiculeAnnonceId === id);

      setSelected((prev) => {
        if (exists) return prev.filter((a) => a.vehiculeAnnonceId !== id);
        if (prev.length >= MAX_SELECTION) return prev;
        return [...prev, annonce];
      });

      if (!exists) {
        trackEvents([
          {
            type: 'request',
            vehiculeAnnonceId: id,
            rank: annonce.aiRank ?? null,
            source: annonce.aiRank ? 'reco' : null,
          },
        ]);
      }
    },
    [trackEvents]
  );

  const clearSelection = useCallback(() => setSelected([]), []);

  const selectedIds = useMemo(() => new Set(selected.map((a) => a.vehiculeAnnonceId)), [selected]);

  const value = useMemo(
    () => ({ selected, selectedIds, toggleSelect, clearSelection, count: selected.length }),
    [selected, selectedIds, toggleSelect, clearSelection]
  );

  return <SelectionContext.Provider value={value}>{children}</SelectionContext.Provider>;
}

// eslint-disable-next-line react-refresh/only-export-components
export default function useSelection() {
  const ctx = useContext(SelectionContext);
  if (!ctx) throw new Error('useSelection doit être utilisé dans <SelectionProvider>');
  return ctx;
}
